import { Check } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * A checkbox.
 *
 * Built on the native input rather than on a Radix primitive, which is the right
 * trade for this control specifically: a checkbox has no behaviour a `<button
 * role="checkbox">` implements better, and the native element already carries
 * keyboard activation, form participation, the indeterminate property and the
 * platform's own accessibility tree. `appearance-none` removes only the paint.
 *
 * The tick is a sibling rather than a child — an input cannot have content — and is
 * revealed through the `peer-checked:` variant, so the checked state is a pure CSS
 * consequence of the input's own state and never has to be mirrored in React.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, ...props }, ref) => (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      <input
        ref={ref}
        type="checkbox"
        className={cn(
          // 6px, not the theme's control radius: that scale is tuned against a 44px
          // button, and applied to a 20px box it rounds the corners into a circle.
          'peer size-5 cursor-pointer appearance-none rounded-[0.375rem] border border-input bg-card',
          'transition-[background-color,border-color,box-shadow] duration-fast ease-out',
          'hover:border-muted-subtle',
          'checked:border-primary checked:bg-primary',
          // The same ring the fields take, so a keyboard user crossing the form sees
          // one focus treatment rather than one per control type.
          'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
      <Check
        className="pointer-events-none absolute size-3 text-primary-foreground opacity-0 transition-opacity duration-fast ease-out peer-checked:opacity-100"
        strokeWidth={3.5}
        aria-hidden="true"
      />
    </span>
  ),
);
Checkbox.displayName = 'Checkbox';
